/**
 * Template Method Selector
 * Allows user to choose how to generate proposal template:
 * 1. Import from Previous Report
 * 2. AI-Derived (Production) - recommended for most proposals
 */

import React from 'react';
import { Card, Button } from '@tracekit/core';
import { FileText, ArrowRight, Sparkles } from 'lucide-react';

export type TemplateMethod = 'upload_rfp' | 'import_report' | 'ai_derived' | 'production_template';

interface TemplateMethodSelectorProps {
  onSelectMethod: (method: TemplateMethod) => void;
  onCancel?: () => void;
}

export function TemplateMethodSelector({
  onSelectMethod,
  onCancel,
}: TemplateMethodSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Choose Template Generation Method
        </h2>
        <p className="text-gray-600 dark:text-white">
          Most proposals use the AI-derived template. You can also reuse a template from a previous report.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Option 1: AI-Derived (Production) - recommended, shown first */}
        <div
          className="cursor-pointer"
          onClick={() => onSelectMethod('production_template')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod('production_template');
            }
          }}
        >
          <Card className="p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-amber-300 dark:hover:border-amber-700 h-full">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-full">
                <Sparkles className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                AI-Derived Template
              </h3>
              <p className="text-sm text-gray-600 dark:text-white">
                Extract evaluation criteria from your RFP and generate a proposal template tailored to this opportunity.
              </p>
              <div className="flex items-center text-amber-600 dark:text-amber-400 text-sm font-medium">
                Generate template from RFP
                <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        </div>

        {/* Option 2: Import from Previous Report */}
        <div
          className="cursor-pointer"
          onClick={() => onSelectMethod('import_report')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod('import_report');
            }
          }}
        >
          <Card className="p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-green-300 dark:hover:border-green-700 h-full">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import from Previous Report
              </h3>
              <p className="text-sm text-gray-600 dark:text-white">
                Reuse the template structure from a past proposal. Good when you want to keep the same section and question layout.
              </p>
              <div className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
                Browse Reports
                <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {onCancel && (
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
